import { useState } from 'react'

import { Box, Button, Divider, Radio } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  AddPaymentRequestsToPaymentForm,
  PaymentsOptionItem,
} from 'modules/banking/components/AddPaymentRequestsToPaymentForm'
import { useAddPaymentRequestsForm } from 'modules/banking/components/AddPaymentRequestsToPaymentForm/useForm'
import { NewBulkPaymentForm } from 'modules/banking/components/CreateBulkPaymentForm'
import { useCreatePaymentFromPaymentRequestsForm } from 'modules/banking/components/CreateBulkPaymentForm/useFormFromPaymentRequests'

export interface PayPaymentRequestsInBulkModalProps {
  onClose: () => void
  onSuccess: (id?: number) => void
  paymentsOptions?: PaymentsOptionItem[]
  paymentRequestIds: number[]
}

export const PayPaymentRequestsInBulkModal = ({
  onClose,
  onSuccess,
  paymentRequestIds,
  paymentsOptions,
}: PayPaymentRequestsInBulkModalProps) => {
  const [formType, setFormType] = useState<'new' | 'existing' | ''>('')
  const hasPaymentsAvailable = !!paymentsOptions?.length

  const { form: creationForm, loading: loadingCreation } = useCreatePaymentFromPaymentRequestsForm({
    onSuccess: onSuccess,
    paymentRequestIds,
  })

  const { form: addToPaymentForm, loading: loadingAddition } = useAddPaymentRequestsForm({
    onSuccess: onSuccess,
    paymentRequestIds,
  })

  const footer = (
    <Box flexDirection="row" gap="s8">
      <Button type="secondary" onClick={onClose}>
        {i18n.t('banking.paymentRequests.pay_in_bulk_modal.actions.cancel')}
      </Button>
      <Button submit disabled={loadingCreation || loadingAddition}>
        {i18n.t('banking.paymentRequests.pay_in_bulk_modal.actions.save')}
      </Button>
    </Box>
  )

  const handleSubmit = async () => {
    track(trackingEvents.accounting.payments.clickSavePayInBulk, {
      bulk_payment_type: formType === 'new' ? 'new_bulk_payment' : 'add_to_existing_payment',
      source: 'payment_requests',
    })

    if (formType === 'new') {
      await creationForm.submitForm()
    } else if (formType === 'existing') {
      await addToPaymentForm.submitForm()
    }
  }

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('banking.paymentRequests.pay_in_bulk_modal.title')}
      description={i18n.t('banking.paymentRequests.pay_in_bulk_modal.description')}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={footer}
    >
      <Box gap="s16" flexDirection="column">
        <Box gap="s8" flexDirection="column">
          <Radio
            name="new_payment"
            description={i18n.t(
              'banking.paymentRequests.pay_in_bulk_modal.options.new.description'
            )}
            option={{
              label: i18n.t('banking.paymentRequests.pay_in_bulk_modal.options.new.title'),
              value: 'new',
              disabled: false,
            }}
            value={formType}
            onChange={() => setFormType('new')}
          />
          <Radio
            name="existing_payment"
            description={i18n.t(
              'banking.paymentRequests.pay_in_bulk_modal.options.existing.description'
            )}
            option={{
              label: i18n.t('banking.paymentRequests.pay_in_bulk_modal.options.existing.title'),
              value: 'existing',
              disabled: !hasPaymentsAvailable,
            }}
            value={formType}
            onChange={() => setFormType('existing')}
          />
        </Box>
        {!!formType && <Divider />}
        {formType === 'new' && <NewBulkPaymentForm form={creationForm} />}
        {hasPaymentsAvailable && formType === 'existing' && (
          <AddPaymentRequestsToPaymentForm
            form={addToPaymentForm}
            paymentsOptions={paymentsOptions}
          />
        )}
      </Box>
    </Modal>
  )
}
